import { useEffect, useMemo, useState } from 'react'
import MainLayout from '@/shared/Layouts/MainLayout'
import css from './aiAgent.module.css'
import SearchInput from '@/shared/Inputs/SearchInput'
import { useRecoilValue } from 'recoil'
import equipmentAtom from '@/pages/Equipment/atoms/equipment.atom.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import ShortEquipmentCard from '@/pages/Equipment/components/Common/ShortEquipmentCard'
import AiIcon from '@/assets/icons/ai-icon.svg?react'
import MessageInput from '@/pages/AIAgent/components/MessageInput'
import MessageItem from '@/pages/AIAgent/components/MessageItem'
import PassportIcon from '@/assets/icons/mdm/passport.svg?react'
import DiagnosticIcon from '@/assets/icons/search.svg?react'
import SettingsIcon from '@/assets/icons/settingsIcon.svg?react'
import InfoIcon from '@/assets/icons/infoIcon.svg?react'
import usePersistentState from '@/shared/hooks/usePersistentState'
import useAiAssistanceMessagingCtrl from '@/pages/AIAgent/hooks/useAiAssistanceMessagingCtrl'
import EquipmentFilterStatus from '@/pages/DeviceMap/components/EquipmentFilterStatus'
import useTitle from '@/shared/hooks/useTitle'

const AIAgentPage = () => {
  useTitle('AI ассистент')
  const equipmentState = useRecoilValue(equipmentAtom)

  const [selectedEquipmentId, setSelectedEquipmentId] = usePersistentState<IEquipment['id']>(
    null,
    'ai-assistant/selected-equipment-id'
  )

  const [search, setSearch] = useState('')
  const [filters, setFilters] = usePersistentState(
    {
      statuses: ['RUNNING', 'ERROR'],
      models: [],
    },
    'ai-assistant/filters'
  )

  const handleFilterChange = (val: any, name: string) => {
    setFilters((prev) => ({ ...prev, [name]: val }))
  }

  const { messages, messagesEndRef, messagesLoading, asking, handleAsk } =
    useAiAssistanceMessagingCtrl(selectedEquipmentId)

  const filteredEquipment = useMemo(() => {
    let list = equipmentState.items || []

    const lower = search.toLowerCase()
    if (lower) {
      list = list.filter((eq) => {
        const address = equipmentUtils.getEquipmentAddress(eq.location).toLowerCase()
        return eq.name.toLowerCase().includes(lower) || address.includes(lower)
      })
    }

    if (filters.statuses.length > 0) {
      list = list.filter((item) => filters.statuses.includes(item.state))
    }
    if (filters.models.length > 0) {
      list = list.filter((item) => filters.models.includes(item.modelId))
    }

    return list
  }, [equipmentState.items, search, filters])

  useEffect(() => {
    if (!selectedEquipmentId && filteredEquipment.length) {
      setSelectedEquipmentId(filteredEquipment[0].id)
    }
  }, [filteredEquipment, selectedEquipmentId])

  const currentEquipment = useMemo(
    () => equipmentState.items.find((eq) => eq.id === selectedEquipmentId),
    [equipmentState.items, selectedEquipmentId]
  )

  const renderMessages = () => {
    if (!currentEquipment) {
      return (
        <div className={css.emptyState}>
          <EmptyPlaceholder inscription="Нет лифта для общения с ассистентом" />
        </div>
      )
    }

    if (messagesLoading) {
      return (
        <div className={css.loaderContainer}>
          <ThreeDotsLoader inscription="Загружаем переписку..." />
        </div>
      )
    }

    if (!messages.length) {
      return (
        <div className={css.perksWrapper}>
          <div className="inline-flex-gap gap8">
            <AiIcon />
            <p className="text-heading-4">AI Лифтер</p>
          </div>
          <div className={css.perksGrid}>
            <div className={css.perkGridItem}>
              <div className="inline-flex-gap gap8 center">
                <PassportIcon />
                <h5 className="text-heading-5">Быстрый поиск решений</h5>
              </div>
              <p className="text-nm">
                Ассистент мгновенно находит нужную информацию в технической документации. Он избавляет от долгого
                листания инструкций и помогает быстро понять, что делать в конкретной ситуации.
              </p>
            </div>
            <div className={css.perkGridItem}>
              <div className="inline-flex-gap gap8 center">
                <DiagnosticIcon style={{ color: 'var(--error)' }} />
                <h5 className="text-heading-5">Диагностика</h5>
              </div>
              <p className="text-nm">
                Опиши проблему — и ассистент предложит вероятные причины и варианты устранения. Это ускоряет поиск
                неисправностей и снижает время простоя лифта.
              </p>
            </div>
            <div className={css.perkGridItem}>
              <div className="inline-flex-gap gap8 center">
                <SettingsIcon style={{ color: 'var(--success)', width: '20px', height: '20px' }} />
                <h5 className="text-heading-5">Подсказки по настройке</h5>
              </div>
              <p className="text-nm">
                Асситент объясняет параметры, режимы и пункты меню контроллера простыми словами. Техник получает чёткие
                пошаговые инструкции прямо на рабочем месте.
              </p>
            </div>
            <div className={css.perkGridItem}>
              <div className="inline-flex-gap gap8 center">
                <InfoIcon style={{ color: 'var(--primary)' }} />
                <h5 className="text-heading-5">Поддержка на объекте</h5>
              </div>
              <p className="text-nm">
                Ассистент работает как личный консультант: отвечает на вопросы, уточняет детали и помогает в
                нестандартных ситуациях.
              </p>
            </div>
            <div className={css.infoGridItem}>
              <p className="text-nm text-center">
                <span className="text-nm-bold">Обратите внимание:</span> асситент работает в контексте выбранного лифта.
                Это важно, поскольку документации к лифтовом оборудованию различаются от производителя к производителю.
                Выбрать лифт, по которому вы хотите задать вопрос можно в панеле справа.
              </p>
            </div>
          </div>
        </div>
      )
    }

    return messages.map((message) => <MessageItem key={message.id} message={message} />)
  }

  const renderEquipmentList = () => {
    if (!filteredEquipment.length) {
      return <EmptyPlaceholder inscription="Не найдено ни одного лифта" />
    }

    return filteredEquipment.map((eq) => {
      return (
        <ShortEquipmentCard
          equipment={eq}
          onClick={() => setSelectedEquipmentId(eq.id)}
          active={eq.id === selectedEquipmentId}
          key={eq.id}
          minified={true}
        />
      )
    })
  }

  return (
    <MainLayout>
      <div className={css.wrapper}>
        <div className="flex-lines gap16 height100">
          <div className={css.layout}>
            <div className={css.chatCardWrapper}>
              <div className={css.chatCard}>
                <div className={css.chatHeader}>
                  <h5 className="text-lg text-center" style={{ color: 'var(--text-secondary-strong)' }}>
                    {currentEquipment ? equipmentUtils.getEquipmentAddress(currentEquipment.location) : ''}
                  </h5>
                </div>

                <div className={css.messages}>
                  <div style={{ height: '80px', display: 'flex', flexShrink: 0 }} />
                  {renderMessages()}
                  <div ref={messagesEndRef} style={{ height: '80px', display: 'flex', flexShrink: 0 }} />
                </div>
                <div className={css.messageInput}>
                  <MessageInput
                    onSend={handleAsk}
                    placeholder="Начните вводить вопрос..."
                    disabled={!currentEquipment || asking}
                    loading={asking}
                  />
                </div>
              </div>
            </div>
            <div className={css.equipmentCardWrapper}>
              <div className={css.equipmentCard}>
                <div className={css.versionWrapper}>
                  <p className="text-sm text-center">Версия: A-1.1.0</p>
                </div>
                <div style={{ paddingRight: '24px', WebkitAlignContent: '100%' }}>
                  <div className="inline-flex-gap width100" style={{ marginBottom: '12px' }}>
                    <EquipmentFilterStatus
                      statuses={filters.statuses as any}
                      name="statuses"
                      onChange={handleFilterChange}
                    />
                  </div>
                  <SearchInput
                    value={search}
                    name="equipment-search"
                    onChange={(val) => setSearch(val)}
                    size="large"
                    placeHolder="Поиск лифтов и адресов"
                  />
                </div>
                <div
                  className="flex-lines gap16 width100"
                  style={{ minHeight: 0, overflow: 'auto', paddingRight: '24px', marginTop: '16px' }}
                >
                  {renderEquipmentList()}
                  <div style={{ paddingBottom: '40px' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default AIAgentPage
